/**
 * Scheduling settings: defaults, schema metadata and resolution rules.
 *
 * Every business rule that we were not 100% certain about lives here as an
 * editable setting rather than as a hard-coded branch in the scheduler. The
 * defaults were derived by reverse-engineering the manually produced lunch
 * schedules for the week commencing 27 July 2026 (see `docs/RULES.md`).
 *
 * Settings resolve in three layers, most specific wins:
 *
 *   base  ->  weekday | weekend  ->  days[dayKey]
 *
 * `resolveDayConfig()` performs that merge. Nothing in the scheduler should
 * read `settings.base` directly.
 */

import { DAY_KEYS, isWeekend, parseTimeToMinutes } from './time.js';

/**
 * Bumped whenever a *default* changes in a way that must reach browsers which
 * already have settings saved. `normaliseSettings` uses it to decide whether
 * stored solver weights are still trustworthy.
 *
 * 2: `scoringWeights.spread` corrected from 4 to 0.5, and `mandatoryShiftLunches`
 *    introduced.
 */
export const SETTINGS_SCHEMA_VERSION = 2;

/**
 * NISCC weekday safeguard: lunch sharing cannot begin before 13:00.
 * This is a fixed compliance rule rather than a manager preference.
 */
export const NISCC_SINGLE_LUNCH_CUTOFF_MINUTES = 13 * 60;

/**
 * Evening safeguard: from 17:00 onwards nobody shares a lunch slot, on any day
 * and whatever the day's cap says. The team thins out through the evening, so
 * two people going at once is a bigger hole then than it is at lunchtime.
 *
 * A break starting at 16:30 finishes at 16:59 and is unaffected; the restriction
 * begins with the 17:00 slot. Where two evening lunches would collide, the
 * second moves to the following slot.
 */
export const EVENING_SINGLE_LUNCH_FROM_MINUTES = 17 * 60;

/** Fixed Saturday evening run requested by the operations team. */
export const SATURDAY_EVENING_START_MINUTES = 18 * 60 + 30;
export const SATURDAY_EVENING_END_MINUTES = 20 * 60;

/** Policies for shifts that sit in the ambiguous "about six hours" band. */
export const SIX_HOUR_POLICIES = Object.freeze({
  NO_LUNCH: 'no-lunch',
  LUNCH: 'lunch',
  FLAG: 'flag',
});

/** Policies for shifts that run past midnight. */
export const OVERNIGHT_POLICIES = Object.freeze({
  NONE: 'none',
  MID_SHIFT: 'mid-shift',
  NIGHT_WINDOW: 'night-window',
});

/** Lunch grouping. `auto` derives the group from the shift start time. */
export const LUNCH_GROUPS = Object.freeze({
  DAY: 'day',
  EVENING: 'evening',
  NIGHT: 'night',
});

/**
 * Coverage/placement rules that can vary by day. Any subset of these keys may
 * appear in the `weekday`, `weekend` and `days.<key>` override layers.
 */
export const DAY_PROFILE_KEYS = Object.freeze([
  'dayWindowStart',
  'dayWindowEnd',
  'eveningWindowStart',
  'eveningWindowEnd',
  'maxSimultaneousLunches',
  'allowSharedSlot',
  'minStaffOnDuty',
  'minMinutesAfterShiftStart',
  'minMinutesBeforeShiftEnd',
  'preferredOffsetMinutes',
  'preferredSpreadMinutes',
]);

/** The base coverage profile - sensible for a busy weekday. */
const BASE_PROFILE = {
  /** Normal daytime lunch window (inclusive of both ends as slot starts). */
  dayWindowStart: '12:00',
  dayWindowEnd: '16:00',
  /** Normal evening lunch window for late/evening staff. */
  eveningWindowStart: '18:00',
  eveningWindowEnd: '20:00',
  /** Hard cap on how many people may be at lunch in the same slot. */
  maxSimultaneousLunches: 2,
  /** When false the cap is forced to 1 regardless of the number above. */
  allowSharedSlot: true,
  /** Hard floor on staff still answering phones during any lunch slot. */
  minStaffOnDuty: 2,
  /** A lunch may not start until this long after the shift begins. */
  minMinutesAfterShiftStart: 120,
  /** A lunch must finish at least this long before the shift ends. */
  minMinutesBeforeShiftEnd: 60,
  /**
   * Where in the shift the ideal lunch sits, measured from the shift start.
   * 270 minutes (4h30) reproduces the observed historical pattern far better
   * than a plain midpoint - see docs/RULES.md.
   */
  preferredOffsetMinutes: 270,
  /**
   * Retained in the saved-settings shape for backwards compatibility. Lunchly's
   * operational policy now always prefers consecutive 30-minute slots.
   */
  preferredSpreadMinutes: 0,
};

/** Relative importance of each scoring term. All are editable in Settings. */
export const DEFAULT_SCORING_WEIGHTS = Object.freeze({
  /** Closeness to the employee's ideal lunch point. */
  preferredTime: 1,
  /** Bonus for landing inside the configured lunch window. */
  windowFit: 1.2,
  /** Reward for leaving more people on the phones. */
  coverage: 2,
  /** Penalty for stacking people into the same slot. */
  simultaneous: 1,
  /** Strongly keep earlier starters on earlier lunches within the compact run. */
  startOrder: 2,
  /** Employee-level early/late/balanced preference. */
  employeePreference: 0.8,
  /**
   * Mild preference for a continuous run.
   *
   * Deliberately well below `preferredTime`. At 4 this term was four times the
   * weight of being at the right point of your own shift, so one cosmetic gap
   * outscored a perfect placement: a 13:00-21:00 shift whose lunch belongs at
   * 16:00 was pulled to 15:30 purely to avoid leaving 15:30 empty. Keeping the
   * run tidy is worth something, but never more than eating at the right time.
   */
  spread: 0.5,
  /** Week-level fairness - who has had the good slots recently. */
  fairness: 0.4,
  /** Support from the historical benchmark patterns. */
  history: 0.3,
});

export const DEFAULT_SETTINGS = Object.freeze({
  schemaVersion: SETTINGS_SCHEMA_VERSION,

  /* --- Lunch shape ------------------------------------------------------ */
  lunchDurationMinutes: 30,
  slotIntervalMinutes: 30,

  /* --- Eligibility ------------------------------------------------------ */
  /**
   * Shifts shorter than this never earn a lunch. 330 (5h30) excludes every
   * five-hour-and-under shift in the benchmark week (09:00-14:00, 08:00-13:00,
   * 09:00-13:30, 08:00-12:00) while leaving six-hour shifts to the policy below.
   */
  minShiftMinutesForLunch: 330,
  /** Durations inside this band are governed by `sixHourPolicy`. */
  ambiguousShiftMinMinutes: 330,
  ambiguousShiftMaxMinutes: 419,
  /**
   * Company rule that was never written down: do six-hour shifts get a lunch?
   * Observed practice is "no, except Alicia", so the default is no-lunch and
   * Alicia carries an explicit employee override.
   */
  sixHourPolicy: SIX_HOUR_POLICIES.NO_LUNCH,
  /**
   * Shift patterns whose lunch time is fixed and must be honoured.
   *
   * These are not preferences. A matching shift takes the stated time even when
   * that slot is already full, because the alternative — sliding the person to
   * 15:30 to respect a cap — is the thing this rule exists to stop. Coverage is
   * still protected: only the cap on *simultaneous lunches* is waived, never the
   * minimum staff on duty, never the shift bounds.
   *
   * `13:00-21:00` ("one to nine") eats at 16:00 on every day it appears in the
   * historical schedules, and Josh confirmed it should always do so.
   */
  mandatoryShiftLunches: [
    { startTime: '13:00', endTime: '21:00', lunchTime: '16:00', label: '13:00-21:00' },
  ],
  /**
   * Whether a mandatory lunch may exceed the day's cap on simultaneous lunches.
   * Turning this off demotes the rule above back to a strong preference.
   */
  mandatoryLunchOverridesSimultaneousCap: true,

  /** Day shifts at least this long use the later long-shift lunch preference. */
  longDayShiftThresholdMinutes: 600,
  /** Soft preferred range for qualifying long daytime shifts. */
  longDayLunchWindowStart: '13:30',
  longDayLunchWindowEnd: '14:30',

  /* --- Overnight -------------------------------------------------------- */
  /**
   * A shift counts as overnight when it finishes strictly after midnight.
   * A 16:00-00:00 shift therefore is *not* overnight (it ends at exactly
   * 1440) which matches how Heather, Jack, Jonny and William are treated.
   */
  overnightEndThresholdMinutes: 1440,
  overnightPolicy: OVERNIGHT_POLICIES.NONE,
  /** Used when `overnightPolicy` is `night-window`. */
  nightWindowStart: '01:00',
  nightWindowEnd: '04:00',

  /* --- Grouping --------------------------------------------------------- */
  /**
   * Shifts starting at or after this time belong to the evening lunch group.
   * 14:00 separates the benchmark cleanly: 13:00-21:00 staff took day-window
   * lunches (16:00) while 14:00-22:00 staff took evening lunches (18:30+).
   */
  eveningGroupStartTime: '14:00',
  /** A shift finishing at or after this also counts as evening. */
  eveningGroupEndTime: '22:00',

  /* --- Coverage profiles ------------------------------------------------ */
  base: { ...BASE_PROFILE },
  weekday: {},
  weekend: {
    // Weekends run a lighter team, so lunches are singled out.
    maxSimultaneousLunches: 1,
    minStaffOnDuty: 1,
    eveningWindowStart: '18:00',
  },
  /** Per-day overrides keyed by `mon`..`sun`; each holds a partial profile. */
  days: {},

  /* --- Scoring ---------------------------------------------------------- */
  scoringWeights: { ...DEFAULT_SCORING_WEIGHTS },
  /** Feed the historical benchmark into the `history` scoring term. */
  useHistoricalHints: true,

  /* --- Search ----------------------------------------------------------- */
  /** Local-search passes used to polish the greedy solution. */
  improvementPasses: 3,
  /** Allow the scheduler to relax soft rules when nothing else fits. */
  allowSoftRuleRelaxation: true,
});

/**
 * Field metadata that drives the Settings UI. Keeping it beside the defaults
 * means a new setting only has to be declared once.
 */
export const SETTINGS_FIELDS = Object.freeze([
  {
    group: 'Lunch shape',
    fields: [
      { key: 'lunchDurationMinutes', label: 'Lunch duration', type: 'number', unit: 'minutes', min: 10, max: 120, step: 5, help: 'How long one lunch break lasts.' },
      { key: 'slotIntervalMinutes', label: 'Slot interval', type: 'number', unit: 'minutes', min: 5, max: 60, step: 5, help: 'Granularity of the lunch grid. 30 matches the historical schedules.' },
    ],
  },
  {
    group: 'Eligibility',
    fields: [
      { key: 'minShiftMinutesForLunch', label: 'Minimum shift length for lunch', type: 'number', unit: 'minutes', min: 0, max: 900, step: 15, help: '330 (5h30) excludes every five-hour-or-shorter shift.' },
      { key: 'sixHourPolicy', label: 'Six-hour shift policy', type: 'select', options: [
        { value: SIX_HOUR_POLICIES.NO_LUNCH, label: 'No lunch' },
        { value: SIX_HOUR_POLICIES.LUNCH, label: 'Give a lunch' },
        { value: SIX_HOUR_POLICIES.FLAG, label: 'Give a lunch and flag for review' },
      ], help: 'An undocumented company rule. Alicia Brennan overrides this via her staff rule.' },
      { key: 'ambiguousShiftMinMinutes', label: 'Ambiguous band - from', type: 'number', unit: 'minutes', min: 0, max: 900, step: 15, help: 'Lower bound of the shift length governed by the six-hour policy.' },
      { key: 'ambiguousShiftMaxMinutes', label: 'Ambiguous band - to', type: 'number', unit: 'minutes', min: 0, max: 900, step: 15, help: 'Upper bound. Shifts longer than this always earn a lunch.' },
      { key: 'mandatoryLunchOverridesSimultaneousCap', label: 'Fixed shift lunches ignore the simultaneous cap', type: 'boolean', help: 'A 13:00-21:00 shift always takes 16:00, even if that slot is already full. Coverage minimums still apply.' },
      { key: 'longDayShiftThresholdMinutes', label: 'Long daytime shift starts at', type: 'number', unit: 'minutes', min: 330, max: 900, step: 30, help: 'Day shifts this long or longer target the later long-shift lunch range.' },
      { key: 'longDayLunchWindowStart', label: 'Long-shift target - from', type: 'time', help: 'Soft preferred start of the lunch range for long daytime shifts.' },
      { key: 'longDayLunchWindowEnd', label: 'Long-shift target - to', type: 'time', help: 'Soft preferred end of the lunch range for long daytime shifts.' },
    ],
  },
  {
    group: 'Overnight shifts',
    fields: [
      { key: 'overnightPolicy', label: 'Overnight break handling', type: 'select', options: [
        { value: OVERNIGHT_POLICIES.NONE, label: 'No scheduled break' },
        { value: OVERNIGHT_POLICIES.MID_SHIFT, label: 'Break near the middle of the shift' },
        { value: OVERNIGHT_POLICIES.NIGHT_WINDOW, label: 'Break inside a night window' },
      ], help: 'Night staff arrange their own breaks by default.' },
      { key: 'nightWindowStart', label: 'Night window start', type: 'time', help: 'Only used by the night-window policy.' },
      { key: 'nightWindowEnd', label: 'Night window end', type: 'time', help: 'Only used by the night-window policy.' },
    ],
  },
  {
    group: 'Grouping',
    fields: [
      { key: 'eveningGroupStartTime', label: 'Evening group starts at', type: 'time', help: 'Shifts starting at or after this time take an evening lunch.' },
      { key: 'eveningGroupEndTime', label: 'Evening group finishes at', type: 'time', help: 'Shifts finishing at or after this time also count as evening.' },
    ],
  },
  {
    group: 'Scheduling behaviour',
    fields: [
      { key: 'useHistoricalHints', label: 'Use historical patterns as a hint', type: 'boolean', help: 'Adds a small score bonus to times this team has historically used.' },
      { key: 'allowSoftRuleRelaxation', label: 'Relax soft rules when nothing fits', type: 'boolean', help: 'Rather than leaving somebody out, place them and raise a warning.' },
      { key: 'improvementPasses', label: 'Improvement passes', type: 'number', min: 0, max: 10, step: 1, help: 'Local-search passes used to polish the first solution.' },
    ],
  },
]);

/** Field metadata for the per-day coverage profile editor. */
export const DAY_PROFILE_FIELDS = Object.freeze([
  { key: 'dayWindowStart', label: 'Day window start', type: 'time' },
  { key: 'dayWindowEnd', label: 'Day window end', type: 'time' },
  { key: 'eveningWindowStart', label: 'Evening window start', type: 'time' },
  { key: 'eveningWindowEnd', label: 'Evening window end', type: 'time' },
  { key: 'maxSimultaneousLunches', label: 'Max simultaneous lunches', type: 'number', min: 1, max: 10, step: 1 },
  { key: 'allowSharedSlot', label: 'Allow two staff to share a slot', type: 'boolean' },
  { key: 'minStaffOnDuty', label: 'Minimum staff on duty', type: 'number', min: 0, max: 40, step: 1 },
  { key: 'minMinutesAfterShiftStart', label: 'Earliest lunch after shift start', type: 'number', unit: 'minutes', min: 0, max: 600, step: 15 },
  { key: 'minMinutesBeforeShiftEnd', label: 'Minimum time left after lunch', type: 'number', unit: 'minutes', min: 0, max: 600, step: 15 },
  { key: 'preferredOffsetMinutes', label: 'Preferred time after starting', type: 'number', unit: 'minutes', min: 0, max: 720, step: 15 },
  { key: 'preferredSpreadMinutes', label: 'Legacy spacing preference', type: 'number', unit: 'minutes', min: 0, max: 0, step: 15 },
]);

/** Deep-ish clone of the default settings (safe to mutate). */
export function createDefaultSettings() {
  return {
    ...DEFAULT_SETTINGS,
    base: { ...DEFAULT_SETTINGS.base },
    weekday: { ...DEFAULT_SETTINGS.weekday },
    weekend: { ...DEFAULT_SETTINGS.weekend },
    days: {},
    scoringWeights: { ...DEFAULT_SETTINGS.scoringWeights },
  };
}

/**
 * Merge stored settings over the defaults, dropping unknown keys and coercing
 * obviously broken values. Never throws - a corrupt LocalStorage payload
 * degrades to defaults rather than breaking the app.
 */
export function normaliseSettings(stored) {
  const base = createDefaultSettings();
  if (!stored || typeof stored !== 'object') return base;

  for (const key of Object.keys(base)) {
    if (key === 'base' || key === 'weekday' || key === 'weekend' || key === 'days' || key === 'scoringWeights') continue;
    if (stored[key] !== undefined && stored[key] !== null) base[key] = stored[key];
  }

  base.base = { ...base.base, ...pickProfile(stored.base) };
  base.weekday = pickProfile(stored.weekday);
  base.weekend = { ...base.weekend, ...pickProfile(stored.weekend) };

  base.days = {};
  if (stored.days && typeof stored.days === 'object') {
    for (const dayKey of DAY_KEYS) {
      const override = pickProfile(stored.days[dayKey]);
      if (Object.keys(override).length > 0) base.days[dayKey] = override;
    }
  }

  /*
   * Solver tuning is migrated, not merged.
   *
   * Scoring weights are internal calibration, not a preference anybody sets on
   * purpose - but they are saved, so a browser keeps whatever the defaults were
   * on the day it first ran Lunchly. That made a corrected default unreachable:
   * a saved `spread` of 4 (the value that dragged a 13:00-21:00 shift off 16:00
   * and onto 15:30) kept overriding the fixed 0.5 on every load, so the fix
   * shipped and nothing changed for anyone who had used the app before.
   *
   * Weights saved by an older schema are therefore discarded in favour of the
   * current defaults. Everything a manager actually chose - windows, coverage
   * floors, policies, per-day overrides, staff rules - is still merged as normal.
   */
  const storedVersion = Number(stored.schemaVersion);
  const weightsAreCurrent = Number.isFinite(storedVersion)
    && storedVersion >= SETTINGS_SCHEMA_VERSION;

  base.scoringWeights = { ...base.scoringWeights };
  if (weightsAreCurrent && stored.scoringWeights && typeof stored.scoringWeights === 'object') {
    for (const key of Object.keys(base.scoringWeights)) {
      const value = Number(stored.scoringWeights[key]);
      if (Number.isFinite(value) && value >= 0) base.scoringWeights[key] = value;
    }
  }

  base.schemaVersion = SETTINGS_SCHEMA_VERSION;
  return base;
}

function pickProfile(source) {
  const result = {};
  if (!source || typeof source !== 'object') return result;
  for (const key of DAY_PROFILE_KEYS) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
      result[key] = source[key];
    }
  }
  return result;
}

/**
 * Resolve the effective coverage profile for one day of the week.
 * Returns a flat object with numeric minute values already parsed, so the
 * scheduler never has to think about "12:00" strings.
 */
export function resolveDayConfig(settings, dayKey) {
  const merged = {
    ...settings.base,
    ...(isWeekend(dayKey) ? settings.weekend : settings.weekday),
    ...(settings.days?.[dayKey] ?? {}),
  };

  const maxSimultaneous = merged.allowSharedSlot === false
    ? 1
    : Math.max(1, Math.round(Number(merged.maxSimultaneousLunches) || 1));

  return {
    dayKey,
    isWeekend: isWeekend(dayKey),
    dayWindow: {
      start: parseTimeToMinutes(merged.dayWindowStart) ?? 720,
      end: parseTimeToMinutes(merged.dayWindowEnd) ?? 960,
    },
    eveningWindow: dayKey === 'sat'
      ? {
        start: SATURDAY_EVENING_START_MINUTES,
        end: SATURDAY_EVENING_END_MINUTES,
      }
      : {
        start: parseTimeToMinutes(merged.eveningWindowStart) ?? 1080,
        end: parseTimeToMinutes(merged.eveningWindowEnd) ?? 1200,
      },
    maxSimultaneousLunches: maxSimultaneous,
    allowSharedSlot: merged.allowSharedSlot !== false,
    minStaffOnDuty: Math.max(0, Math.round(Number(merged.minStaffOnDuty) || 0)),
    minMinutesAfterShiftStart: Math.max(0, Number(merged.minMinutesAfterShiftStart) || 0),
    minMinutesBeforeShiftEnd: Math.max(0, Number(merged.minMinutesBeforeShiftEnd) || 0),
    preferredOffsetMinutes: Math.max(0, Number(merged.preferredOffsetMinutes) || 0),
    preferredSpreadMinutes: Math.max(0, Number(merged.preferredSpreadMinutes) || 0),
    raw: merged,
  };
}

/**
 * The simultaneous-lunch limit at one instant.
 *
 * Weekdays are always restricted to one person before 13:00 for NISCC
 * compliance. At 13:00 and later, and throughout weekends, the resolved day
 * profile applies as normal.
 */
export function maxSimultaneousLunchesAt(dayConfig, minutes) {
  const configured = Math.max(
    1,
    Math.round(Number(dayConfig?.maxSimultaneousLunches) || 1),
  );
  if (dayConfig?.isWeekend !== true
    && Number.isFinite(minutes)
    && minutes < NISCC_SINGLE_LUNCH_CUTOFF_MINUTES) {
    return 1;
  }
  if (Number.isFinite(minutes) && minutes >= EVENING_SINGLE_LUNCH_FROM_MINUTES) {
    return 1;
  }
  return configured;
}

/** True when a day carries any explicit override (drives the UI badge). */
export function hasDayOverride(settings, dayKey) {
  return Boolean(settings.days?.[dayKey] && Object.keys(settings.days[dayKey]).length > 0);
}
